import { normalizeThicknessName, slugifyFilterId } from '../utils/filterIds';

/**
 * Manages syncing filter state with the URL query parameters.
 */
export function useSlabUrl() {

    // Default values — only write to URL when value differs from default
    const DIMENSION_DEFAULTS = { minWidth: 0, maxWidth: 150, minHeight: 0, maxHeight: 100 };
    const COUNT_DEFAULTS = { stockCount: 0, availableCount: 0 };
    const ORDER_DEFAULT = 'a-z';

    const saveStateToUrl = ({ search, activeFilters, toggles, dimensions, counts, order }) => {
        const url = new URL(window.location);

        if (search.value) {
            url.searchParams.set('search', search.value);
        } else {
            url.searchParams.delete('search');
        }

        Object.entries(activeFilters).forEach(([key, values]) => {
            if (values && values.length > 0) {
                url.searchParams.set(key, values.join(','));
            } else {
                url.searchParams.delete(key);
            }
        });

        // Toggles
        const toggleMap = {
            'new_arrivals': toggles.onlyNewArrivals,
            'sales': toggles.onlySales,
            'singles': toggles.onlySingles,
            'singles_exact': toggles.singlesExact,
            'doubles': toggles.onlyDoubles,
            'dealer': toggles.onlyDealerItems,
            'signature': toggles.onlySignatureItems,
            'stock': toggles.onlyStockItems,
            'domestic': toggles.onlyDomesticItems,
            'include_holds': toggles.includeHolds,
        };

        Object.entries(toggleMap).forEach(([param, refVal]) => {
            if (refVal && refVal.value) url.searchParams.set(param, '1');
            else url.searchParams.delete(param);
        });

        // Dimensions (only write non-default values)
        if (dimensions) {
            const dimMap = {
                'minWidth': dimensions.minWidth,
                'maxWidth': dimensions.maxWidth,
                'minHeight': dimensions.minHeight,
                'maxHeight': dimensions.maxHeight,
            };

            Object.entries(dimMap).forEach(([param, refVal]) => {
                if (refVal && Number(refVal.value) !== DIMENSION_DEFAULTS[param]) {
                    url.searchParams.set(param, refVal.value);
                } else {
                    url.searchParams.delete(param);
                }
            });
        }

        // Counts (only write non-default values)
        if (counts) {
            const countMap = {
                'stockCount': counts.stockCount,
                'availableCount': counts.availableCount,
            };

            Object.entries(countMap).forEach(([param, refVal]) => {
                if (refVal && Number(refVal.value) !== COUNT_DEFAULTS[param]) {
                    url.searchParams.set(param, refVal.value);
                } else {
                    url.searchParams.delete(param);
                }
            });
        }

        // Sort order (only write non-default)
        if (order && order.value && order.value !== ORDER_DEFAULT) {
            url.searchParams.set('order', order.value);
        } else {
            url.searchParams.delete('order');
        }

        history.replaceState({}, "Products", url.toString());
    };

    const loadStateFromUrl = ({ search, activeFilters, filters, filterKeys, toggles, dimensions, counts, order }) => {
        const url = new URL(window.location);

        const s = url.searchParams.get('search');
        if (s) search.value = s;

        const availableFilters = filters?.value ?? filters ?? {};
        const configuredFilterKeys = filterKeys?.value ?? filterKeys ?? [];
        const availableFilterKeys = [...new Set([
            ...configuredFilterKeys,
            ...Object.keys(availableFilters),
            ...Object.keys(activeFilters),
        ])];
        const resolveFilterName = (key, filterValue) => {
            const normalizedValue = key === 'thickness'
                ? normalizeThicknessName(filterValue)
                : String(filterValue).trim();
            const valueId = slugifyFilterId(normalizedValue);
            const matchingFilter = (availableFilters[key] || []).find(filter =>
                slugifyFilterId(filter.id) === valueId || slugifyFilterId(filter.name) === valueId
            );

            return matchingFilter?.name || normalizedValue;
        };

        availableFilterKeys.forEach(key => {
            const value = url.searchParams.get(key);
            if (value) {
                activeFilters[key] = value
                    .split(',')
                    .map(filterValue => resolveFilterName(key, filterValue))
                    .filter(Boolean);
            }
        });

        const customSearch = document.querySelector('meta[name="mm-custom-search"]')?.getAttribute('content') || window.customSearch;
        if (customSearch) {
            search.value = customSearch;
        }
        const customCategory = document.querySelector('meta[name="mm-custom-category"]')?.getAttribute('content') || window.customCategory;
        if (customCategory) {
            activeFilters['stoneType'] = customCategory
                .split(',')
                .map(filterValue => resolveFilterName('stoneType', filterValue))
                .filter(Boolean);
        }

        const toggleMap = {
            'new_arrivals': toggles.onlyNewArrivals,
            'sales': toggles.onlySales,
            'singles': toggles.onlySingles,
            'singles_exact': toggles.singlesExact,
            'doubles': toggles.onlyDoubles,
            'dealer': toggles.onlyDealerItems,
            'signature': toggles.onlySignatureItems,
            'stock': toggles.onlyStockItems,
            'domestic': toggles.onlyDomesticItems,
            'include_holds': toggles.includeHolds,
        };

        Object.entries(toggleMap).forEach(([param, refVal]) => {
            if (refVal) {
                refVal.value = url.searchParams.get(param) === '1';
            }
        });

        if (toggles.onlyDoubles?.value) {
            toggles.onlySingles.value = false;
            toggles.singlesExact.value = false;
        }

        // Dimensions
        if (dimensions) {
            const dimMap = {
                'minWidth': dimensions.minWidth,
                'maxWidth': dimensions.maxWidth,
                'minHeight': dimensions.minHeight,
                'maxHeight': dimensions.maxHeight,
            };

            Object.entries(dimMap).forEach(([param, refVal]) => {
                const val = url.searchParams.get(param);
                if (val !== null && refVal) {
                    refVal.value = Number(val);
                }
            });
        }

        // Counts
        if (counts) {
            const countMap = {
                'stockCount': counts.stockCount,
                'availableCount': counts.availableCount,
            };

            Object.entries(countMap).forEach(([param, refVal]) => {
                const val = url.searchParams.get(param);
                if (val !== null && refVal) {
                    refVal.value = Number(val);
                }
            });
        }

        // Sort order
        if (order) {
            const val = url.searchParams.get('order');
            if (val) {
                order.value = val;
            }
        }
    };

    return {
        saveStateToUrl,
        loadStateFromUrl
    };
}
